import { useCallback, useEffect, useState } from 'react'
import { authService } from '../services/authService'
import { listOpenEvents } from '../services/predictionService'

const REFRESH_MS = 60_000

type PredictionEventsUpdatedDetail = {
  action?: string
  userEntryVisible?: boolean
  userVisible?: boolean
  tenantId?: string
  timestamp?: string
}

export function usePredictionUserEntry(predictionEnabled?: boolean) {
  const [hasOpenEvents, setHasOpenEvents] = useState(false)
  const [userEntryVisible, setUserEntryVisible] = useState(true)
  const [checked, setChecked] = useState(false)

  const refresh = useCallback(async () => {
    if (predictionEnabled !== true || !authService.isAuthenticated()) {
      setHasOpenEvents(false)
      setChecked(true)
      return
    }

    try {
      const res = await listOpenEvents()
      setHasOpenEvents((res.data?.length ?? 0) > 0)
    } catch {
      setHasOpenEvents(false)
    } finally {
      setChecked(true)
    }
  }, [predictionEnabled])

  useEffect(() => {
    setChecked(false)
    void refresh()
    const timer = window.setInterval(() => {
      void refresh()
    }, REFRESH_MS)

    const onPredictionEventsUpdated = (event: Event) => {
      const detail = (event as CustomEvent<PredictionEventsUpdatedDetail>).detail
      if (detail?.action === 'user_entry' && typeof detail.userEntryVisible === 'boolean') {
        setUserEntryVisible(detail.userEntryVisible)
        if (!detail.userEntryVisible) {
          setHasOpenEvents(false)
          setChecked(true)
          return
        }
      }
      void refresh()
    }
    window.addEventListener('prediction_events_updated', onPredictionEventsUpdated)

    return () => {
      window.clearInterval(timer)
      window.removeEventListener('prediction_events_updated', onPredictionEventsUpdated)
    }
  }, [refresh])

  return {
    showPredictionEntry: predictionEnabled === true && userEntryVisible && hasOpenEvents,
    hasOpenEvents,
    userEntryVisible,
    checked,
    refresh,
  }
}
